import './view.scss';

const debounce = (fn, wait) => {
    let t = null;
    return (...args) => {
        clearTimeout(t);
        t = setTimeout(() => fn(...args), wait);
    };
};

const buildSequences = (track, containerWidth) => {
    if (!track) return 0;

    let baseHtml = track.getAttribute('data-base-html');
    if (!baseHtml) {
        baseHtml = track.innerHTML;
        track.setAttribute('data-base-html', baseHtml);
    }
    if (!baseHtml) return 0;

    const minWidth = containerWidth * 2;

    const measurer = document.createElement('div');
    measurer.style.position = 'absolute';
    measurer.style.left = '-9999px';
    measurer.style.top = '-9999px';
    measurer.style.visibility = 'hidden';
    measurer.style.pointerEvents = 'none';
    document.body.appendChild(measurer);

    const sample = document.createElement('div');
    sample.className = 'scroll-text__sequence';
    sample.innerHTML = baseHtml;
    measurer.appendChild(sample);

    const singleWidth = sample.offsetWidth || sample.getBoundingClientRect().width || 0;

    document.body.removeChild(measurer);

    const safeSingleWidth = singleWidth > 0 ? singleWidth : Math.max(50, Math.floor(containerWidth / 4));

    let required = Math.max(2, Math.ceil(minWidth / safeSingleWidth));

    if (required % 2 === 1) required += 1;

    const MAX_SEQUENCES = 50;
    if (required > MAX_SEQUENCES) required = MAX_SEQUENCES;

    const existing = Array.from(track.children).filter((n) => n.classList && n.classList.contains('scroll-text__sequence'));
    if (existing.length === required) return required;

    track.innerHTML = '';
    for (let i = 0; i < required; i++) {
        const seq = document.createElement('div');
        seq.className = 'scroll-text__sequence';
        seq.innerHTML = baseHtml;
        track.appendChild(seq);
    }

    return required;
};

const initScrollText = (block) => {
    if (!block || !(block instanceof Element)) return;

    const track = block.querySelector('.scroll-text__track');
    if (!track) return;

    if (block.__scrollTextSetup) return;
    block.__scrollTextSetup = true;

    block.__scrollTextLast = block.__scrollTextLast || { containerWidth: 0, seqCount: 0 };
    block.__scrollTextBuilding = block.__scrollTextBuilding || false;
    block.__scrollTextBuildCount = block.__scrollTextBuildCount || 0;
    block.__scrollTextLastBuildTime = block.__scrollTextLastBuildTime || 0;

    const BUILD_COOLDOWN_MS = 200;
    const BUILD_COUNT_WINDOW_MS = 4000;
    const MAX_BUILDS_IN_WINDOW = 6;

    const WIDTH_TOLERANCE = 1;

    const doBuild = () => {
        if (!document.body.contains(block)) return;

        const now = Date.now();
        if (now - (block.__scrollTextLastBuildTime || 0) > BUILD_COUNT_WINDOW_MS) {
            block.__scrollTextBuildCount = 0;
        }

        if (block.__scrollTextBuildCount >= MAX_BUILDS_IN_WINDOW && (now - (block.__scrollTextLastBuildTime || 0)) <= BUILD_COUNT_WINDOW_MS) {
            return;
        }

        if (now - (block.__scrollTextLastBuildTime || 0) < BUILD_COOLDOWN_MS && block.__scrollTextBuildCount > 0) {
            return;
        }

        const containerWidth = block.offsetWidth || window.innerWidth || 0;
        if (!containerWidth) return;

        if (block.classList.contains('scroll-text--is-ready') && Math.abs((block.__scrollTextLast.containerWidth || 0) - containerWidth) <= WIDTH_TOLERANCE) {
            return;
        }

        if (block.__scrollTextBuilding) return;
        block.__scrollTextBuilding = true;

        const seqCount = buildSequences(track, containerWidth) || 0;
        block.classList.add('scroll-text--is-ready');

        block.__scrollTextLast.containerWidth = containerWidth;
        block.__scrollTextLast.seqCount = seqCount;

        block.__scrollTextBuildCount = (block.__scrollTextBuildCount || 0) + 1;
        block.__scrollTextLastBuildTime = Date.now();

        setTimeout(() => {
            if (Date.now() - block.__scrollTextLastBuildTime >= BUILD_COUNT_WINDOW_MS) {
                block.__scrollTextBuildCount = 0;
            }
        }, BUILD_COUNT_WINDOW_MS + 50);

        block.__scrollTextBuilding = false;
    };

    let ro;
    if (typeof ResizeObserver !== 'undefined') {
        ro = new ResizeObserver(debounce((entries) => {
            for (const entry of entries) {
                if (entry.target !== block) continue;

                const w = (entry.contentRect && entry.contentRect.width) || block.offsetWidth || 0;

                if (Math.abs((block.__scrollTextLast.containerWidth || 0) - w) <= WIDTH_TOLERANCE) continue;

                if (!block.__scrollTextBuilding) doBuild();
            }
        }, 150));
        ro.observe(block);
    }

    const onWinResize = debounce(() => {
        const containerWidth = block.offsetWidth || window.innerWidth || 0;
        if (!containerWidth) return;
        if (Math.abs((block.__scrollTextLast.containerWidth || 0) - containerWidth) <= WIDTH_TOLERANCE) return;
        doBuild();
    }, 150);
    window.addEventListener('resize', onWinResize);

    doBuild();

    block.__scrollTextCleanup = () => {
        if (ro) ro.disconnect();
        window.removeEventListener('resize', onWinResize);
        block.__scrollTextSetup = false;
    };
};

const initAllScrollTextBlocks = () => {
    document.querySelectorAll('.scroll-text').forEach(initScrollText);
};

if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAllScrollTextBlocks);
} else {
    initAllScrollTextBlocks();
}

if (typeof MutationObserver !== 'undefined') {
    const mo = new MutationObserver(debounce(() => {
        initAllScrollTextBlocks();
    }, 250));
    mo.observe(document.body, { childList: true, subtree: true });
}
